import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';
import './HomePage.css';

// Safety Exit functionality
const quickExit = () => {
  try {
    window.location.replace('https://www.google.com');
  } catch (e) {
    window.location.href = 'about:blank';
  }
};

const HomePage: React.FC = () => {
  const { t } = useLanguage();
  const [hasFormInProgress, setHasFormInProgress] = useState(false);

  // Check if there's a form in progress
  useEffect(() => {
    const checkFormProgress = () => {
      try {
        const saved = localStorage.getItem("njadvri_form_v1");
        if (saved) {
          const parsed = JSON.parse(saved);
          // Check if form is not completed (step !== -1 means not completed)
          if (parsed.step !== undefined && parsed.step !== -1) {
            setHasFormInProgress(true);
          } else {
            setHasFormInProgress(false);
          }
        } else {
          setHasFormInProgress(false);
        }
      } catch (e) {
        setHasFormInProgress(false);
      }
    };

    checkFormProgress();
    // Also check when window gains focus (user might have completed form in another tab)
    window.addEventListener('focus', checkFormProgress);
    return () => window.removeEventListener('focus', checkFormProgress);
  }, []);

  // Add keyboard support for safety exit (Escape key)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        quickExit();
      }
    };

    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => document.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, []);

  return (
    <div className="home-page">
      {/* Safety Exit Button */}
      <div className="safety-exit">
        <a href="https://www.google.com" className="safety-exit-link">
          {t('home.safetyExit')}
        </a>
      </div>

      {/* Language Switcher */}
      <div className="home-language-switcher">
        <LanguageSwitcher />
      </div>

      {/* Hero Section */}
      <header className="hero-section">
        <div className="hero-content">
          <div className="hero-visual">
            <img
              src="/owg-cat.jpg"
              alt="Pisica albastră logo"
              className="hero-logo"
            />
          </div>
          <div className="hero-text">
            <h1 className="hero-title">
              {t('home.hero.title')}
            </h1>
            <p className="hero-subtitle">
              {t('home.hero.subtitle')}
            </p>
            <div className="hero-actions">
              <Link to="/evaluare" className="cta-button primary">
                {hasFormInProgress ? t('home.hero.continueAssessment') : t('home.hero.startAssessment')}
              </Link>
              <a href="#about" className="cta-button secondary">
                {t('home.hero.learnMore')}
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <h2 className="section-title">{t('home.about.title')}</h2>
          <p className="about-description">
            {t('home.about.description')}
          </p>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🛡️</div>
              <h3>{t('home.features.safety.title')}</h3>
              <p>{t('home.features.safety.description')}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>{t('home.features.assessment.title')}</h3>
              <p>{t('home.features.assessment.description')}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🌍</div>
              <h3>{t('home.features.multilingual.title')}</h3>
              <p>{t('home.features.multilingual.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Helps Section */}
      <section className="help-section">
        <div className="container">
          <h2 className="section-title">{t('home.help.title')}</h2>
          <div className="help-content">
            <div className="help-text">
              <p className="help-description">
                {t('home.help.description')}
              </p>
              <ul className="help-list">
                <li>{t('home.help.bullet1')}</li>
                <li>{t('home.help.bullet2')}</li>
                <li>{t('home.help.bullet3')}</li>
                <li>{t('home.help.bullet4')}</li>
                <li>{t('home.help.bullet5')}</li>
              </ul>
            </div>
            <div className="help-visual">
              <div className="help-card">
                <h3>{t('home.help.cardTitle')}</h3>
                <p>{t('home.help.cardDescription')}</p>
                <Link to="/evaluare" className="help-button">
                  {t('home.help.startNow')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Resources */}
      <section id="support" className="support-section">
        <div className="container">
          <h2 className="section-title">{t('home.support.title')}</h2>
          <div className="support-grid">
            <div className="support-item">
              <h3>{t('home.support.emergency.title')}</h3>
              <p>{t('home.support.emergency.description')}</p>
              <a href="tel:112" className="support-link emergency">
                {t('home.support.emergency.phone')}
              </a>
            </div>
            <div className="support-item">
              <h3>{t('home.support.hotline.title')}</h3>
              <p>{t('home.support.hotline.description')}</p>
              <a href="tel:0800-500-333" className="support-link">
                {t('home.support.hotline.phone')}
              </a>
            </div>
            <div className="support-item">
              <h3>{t('home.support.online.title')}</h3>
              <p>{t('home.support.online.description')}</p>
              <Link to="/resurse" className="support-link">
                {t('home.support.online.link')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about-us" className="about-us-section">
        <div className="container">
          <h2 className="section-title">{t('home.aboutUsSection.title')}</h2>
          <p className="about-us-text">
            {t('home.aboutUsSection.description')}
          </p>
        </div>
      </section>

      <div className="global-warning">
        {t('home.warning')}
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h4>{t('home.footer.resources')}</h4>
              <p className="footer-about-summary">
                {t('home.footer.description')}
              </p>
            </div>
            <div className="footer-section">
              <h4>{t('home.footer.contact')}</h4>
              <p>{t('home.footer.contactInfo')}</p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} {t('home.footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
